import { gql } from "@apollo/client";

export const GET_GIAS_REFRESH_STATUS = gql`
  query GiasRefreshStatus {
    giasRefreshStatus {
      lastCompletedAt
      currentJob {
        id
        status
        progress
        message
      }
    }
  }
`;

export const MARKETING_GRAPH = gql`
  fragment MarketingTemplateFields on EmailTemplateType {
    id
    order
    name
    subject
    bodyHtml
    bodyText
    contentDocument
    delayHours
    lineageKey
    isClone
    sourceLabel
    sendToMigratedStageParticipants
    isEnabled
    ctaLabel
    ctaUrl
    utmSource
    utmMedium
    utmCampaign
    utmContent
    useBrandedLayout
    sender {
      id
      name
      displayName
      fromEmail
      replyToEmail
      isActive
      dailySendLimit
      todayReserved
      todayConsumed
      todayRemaining
      allocations {
        id
        percentage
        workflow {
          id
          name
          workflowTypeLabel
        }
        workflowName
        workflowTypeLabel
      }
      credentialsConfigured
      signatureDocument
      signatureImageUrl
      signatureImageWidth
      signatureImageLinkUrl
    }
  }
  fragment MarketingVersionFields on VersionType {
    id
    versionNumber
    status
    isPublished
    publishedAt
    stoppedAt
    archivedAt
    stages {
      id
      key
      name
      description
      order
      isEntry
      isTerminal
      advanceAfterEmailSequence
      criterionCode
      criterionConfig
      canvasX
      canvasY
      templates {
        ...MarketingTemplateFields
      }
    }
    transitions {
      id
      outcome
      sourceStage {
        id
        key
      }
      targetStage {
        id
        key
      }
    }
  }
  fragment MarketingWorkflowFields on WorkflowType {
    id
    name
    description
    workflowType
    recipientKind
    selectionModes
    workflowTypeLabel
    isEnabled
    sendFirstEmailOnEnrollment
    sendWindowStart
    sendWindowEnd
    sendTimezone
    activeWeekdays
    senders {
      id
      name
      displayName
      fromEmail
      replyToEmail
      isActive
      dailySendLimit
      todayReserved
      todayConsumed
      todayRemaining
      allocations {
        id
        percentage
        workflow {
          id
          name
          workflowTypeLabel
        }
        workflowName
        workflowTypeLabel
      }
      credentialsConfigured
      signatureDocument
      signatureImageUrl
      signatureImageWidth
      signatureImageLinkUrl
    }
    versions {
      ...MarketingVersionFields
    }
    defaultVersion {
      id
      versionNumber
    }
    currentPublishedVersion {
      ...MarketingVersionFields
    }
    draftVersion {
      ...MarketingVersionFields
    }
  }
`;

export const GET_MARKETING_MANAGEMENT = gql`
  ${MARKETING_GRAPH}
  query MarketingManagement {
    marketingAutomationSettings {
      isEnabled
      pausedAt
      pauseReason
      updatedAt
    }
    marketingWorkflows {
      ...MarketingWorkflowFields
    }
    marketingWorkflowTypes {
      code
      label
      description
      automaticEnrollment
      recipientKind
      selectionModes
      personalisationFields
      supportedCriteria {
        code
        label
        transitionOutcome
        options {
          key
          type
          required
          default
          minimum
          readOnly
          helpText
        }
      }
    }
    marketingCriteria {
      code
      label
      transitionOutcome
      options {
        key
        type
        required
        default
        minimum
        readOnly
        helpText
      }
    }
    marketingSenders {
      id
      name
      displayName
      fromEmail
      replyToEmail
      isActive
      dailySendLimit
      todayReserved
      todayConsumed
      todayRemaining
      allocations {
        id
        percentage
        workflow {
          id
          name
          workflowTypeLabel
        }
        workflowName
        workflowTypeLabel
      }
      credentialsConfigured
      signatureDocument
      signatureImageUrl
      signatureImageWidth
      signatureImageLinkUrl
    }
    marketingUtmSuggestions {
      sources
      mediums
      campaigns
      contents
    }
  }
`;

export const GET_OUTREACH_ENRICHMENT_BATCHES = gql`
  query OutreachEnrichmentBatches($first: Int, $after: String) {
    outreachEnrichmentBatches(first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          status
          batchLimit
          attemptEmailValidation
          requestedAt
          startedAt
          completedAt
          cancelledAt
          searchCallCount
          inputTokenCount
          outputTokenCount
          estimatedCostUsd
          counts
          subjects {
            id
            name
          }
          job {
            id
            status
            progress
            message
          }
          validationJob {
            id
            status
            progress
            message
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_OUTREACH_CONTACTS = gql`
  query OutreachContacts(
    $first: Int
    $after: String
    $search: String
    $verificationStatus: String
    $schoolId: ID
    $localAuthorityId: ID
    $subjectId: ID
  ) {
    outreachContacts(
      first: $first
      after: $after
      search: $search
      verificationStatus: $verificationStatus
      schoolId: $schoolId
      localAuthorityId: $localAuthorityId
      subjectId: $subjectId
    ) {
      edges {
        cursor
        node {
          id
          email
          firstName
          lastName
          jobTitle
          discoveryType
          confidence
          verificationStatus
          convertedAt
          validationProvider
          validationStatus
          validationScore
          validationCheckedAt
          lifecycle
          conversionReason
          hasActiveOutreach
          convertedUser {
            id
            email
            firstName
            lastName
          }
          discoveredAt
          school {
            id
            name
            localAuthority {
              id
              name
              code
            }
          }
          subjects {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_OUTREACH_ENRICHMENT_ATTEMPTS = gql`
  query OutreachEnrichmentAttempts($batchId: ID!, $first: Int, $after: String) {
    outreachEnrichmentAttempts(
      batchId: $batchId
      first: $first
      after: $after
    ) {
      edges {
        cursor
        node {
          id
          status
          diagnosticCode
          errorCategory
          candidateCount
          contactCount
          publiclyConfirmedCount
          inferredUnverifiedCount
          inferredVerifiedCount
          inferredInvalidCount
          totalConfirmedCount
          provider
          providerModel
          searchCallCount
          inputTokenCount
          outputTokenCount
          estimatedCostUsd
          retryCount
          startedAt
          completedAt
          school {
            id
            name
            website
            phaseOfEducationDisplay
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_OUTREACH_ENRICHMENT_ATTEMPT_CONTACTS = gql`
  query OutreachEnrichmentAttemptContacts(
    $attemptId: ID!
    $first: Int
    $after: String
  ) {
    outreachContacts(attemptId: $attemptId, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          email
          firstName
          lastName
          jobTitle
          discoveryType
          confidence
          verificationStatus
          convertedAt
          validationStatus
          validationScore
          validationCheckedAt
          evidenceMetadata
          evidenceUrl
          evidenceDetail
          discoveredAt
          school {
            id
            name
          }
          subjects {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_OUTREACH_ENRICHMENT_SELECTION_SUMMARY = gql`
  query OutreachEnrichmentSelectionSummary(
    $schoolIds: [ID!]
    $schoolFilters: EnrichmentSchoolFiltersInput
    $excludedSchoolIds: [ID!]
    $subjectIds: [ID!]!
  ) {
    outreachEnrichmentSelectionSummary(
      schoolIds: $schoolIds
      schoolFilters: $schoolFilters
      excludedSchoolIds: $excludedSchoolIds
      subjectIds: $subjectIds
    ) {
      total
      availableTotal
      neverAttempted
      successful
      noLead
      failed
      cancelled
      active
      notAlreadyEnriched
      estimatedAvailableCostUsd
      estimatedNotAlreadyEnrichedCostUsd
    }
  }
`;

export const GET_OUTREACH_ENRICHMENT_BATCH = gql`
  query OutreachEnrichmentBatch(
    $batchId: ID!
    $attemptFirst: Int
    $attemptAfter: String
    $contactFirst: Int
    $contactAfter: String
  ) {
    outreachEnrichmentBatch(id: $batchId) {
      id
      status
      requestedAt
      startedAt
      completedAt
      cancelledAt
      counts
      searchCallCount
      inputTokenCount
      outputTokenCount
      estimatedCostUsd
      subjects {
        id
        name
      }
      job {
        id
        status
        progress
        message
      }
    }
    outreachEnrichmentAttempts(
      batchId: $batchId
      first: $attemptFirst
      after: $attemptAfter
    ) {
      edges {
        cursor
        node {
          id
          status
          diagnosticCode
          errorCategory
          candidateCount
          contactCount
          provider
          providerModel
          searchCallCount
          inputTokenCount
          outputTokenCount
          estimatedCostUsd
          retryCount
          school {
            id
            name
            website
            phaseOfEducationDisplay
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
    outreachContacts(
      batchId: $batchId
      first: $contactFirst
      after: $contactAfter
    ) {
      edges {
        cursor
        node {
          id
          email
          firstName
          lastName
          jobTitle
          discoveryType
          confidence
          verificationStatus
          convertedAt
          evidenceMetadata
          evidenceUrl
          evidenceDetail
          discoveredAt
          school {
            id
            name
          }
          subjects {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const SEARCH_MARKETING_USERS = gql`
  query SearchMarketingUsers(
    $workflowId: ID!
    $versionId: ID
    $search: String
    $first: Int
    $after: String
  ) {
    marketingEligibleUsers(
      workflowId: $workflowId
      versionId: $versionId
      search: $search
      first: $first
      after: $after
    ) {
      edges {
        cursor
        node {
          id
          email
          firstName
          lastName
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const SEARCH_MARKETING_CONTACTS = gql`
  query SearchMarketingContacts(
    $workflowId: ID!
    $search: String
    $first: Int
    $after: String
  ) {
    marketingEligibleContacts(
      workflowId: $workflowId
      search: $search
      first: $first
      after: $after
    ) {
      edges {
        cursor
        node {
          id
          email
          firstName
          lastName
          jobTitle
          school {
            id
            name
            phaseOfEducationDisplay
          }
          subjects {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`;

export const GET_PUBLISHED_WORKFLOW_METRICS = gql`
  query PublishedWorkflowMetrics($workflowId: ID!, $versionId: ID) {
    marketingPublishedWorkflowMetrics(
      workflowId: $workflowId
      versionId: $versionId
    ) {
      versionId
      active
      completed
      stopped
      ineligible
      olderVersionActive
      stages {
        stageId
        active
        templates {
          templateId
          waiting
          queued
          sending
          sent
          failed
          skipped
        }
      }
    }
  }
`;

export const GET_MARKETING_MIGRATION_PREVIEW = gql`
  query MarketingMigrationPreview(
    $sourceVersionId: ID!
    $destinationWorkflowId: ID!
  ) {
    marketingMigrationPreview(
      sourceVersionId: $sourceVersionId
      destinationWorkflowId: $destinationWorkflowId
    ) {
      sourceVersionId
      destinationVersionId
      considered
      eligible
      active
      completed
      enrolled
      alreadyEnrolled
      excluded
    }
  }
`;

export const GET_MARKETING_ENROLLMENTS = gql`
  query MarketingEnrollments(
    $workflowId: ID!
    $versionId: ID
    $status: String
    $email: String
    $first: Int
    $after: String
  ) {
    marketingEnrollments(
      workflowId: $workflowId
      versionId: $versionId
      status: $status
      email: $email
      first: $first
      after: $after
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          recipientType
          recipientEmail
          suspensionReason
          suspendedAt
          recipientName
          recipientSchool
          recipientSchoolPhase
          recipientSubjects {
            id
            name
          }
          previousStageCompleted
          status
          enrolledAt
          currentStage {
            id
            name
          }
          assignedSender {
            id
            displayName
            fromEmail
          }
          continuationHistory
          stageHistory {
            id
            sequence
            enteredAt
            exitedAt
            exitReason
            stage {
              id
              name
            }
          }
          deliveries {
            id
            status
            templateName
            scheduledFor
            sentAt
            failedAt
            diagnosticCode
            retryable
          }
        }
      }
    }
  }
`;

export const GET_MARKETING_DELIVERIES = gql`
  query MarketingDeliveries(
    $workflowId: ID!
    $versionId: ID
    $status: String
    $email: String
    $diagnosticCode: String
    $first: Int
    $after: String
  ) {
    marketingDeliveries(
      workflowId: $workflowId
      versionId: $versionId
      status: $status
      email: $email
      diagnosticCode: $diagnosticCode
      first: $first
      after: $after
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          recipientType
          recipientEmail
          recipientName
          workflowName
          stageName
          templateName
          status
          attempts
          scheduledFor
          queuedAt
          sentAt
          failedAt
          skippedAt
          lastAttemptAt
          diagnosticCode
          errorSummary
          senderDisplayName
          senderFromEmail
          retryable
          retryUnavailableReason
        }
      }
    }
  }
`;

export const GET_MARKETING_OPERATIONAL_METRICS = gql`
  query MarketingOperationalMetrics($workflowId: ID, $versionId: ID) {
    marketingOperationalMetrics(
      workflowId: $workflowId
      versionId: $versionId
    ) {
      enrolled
      active
      completed
      stopped
      ineligible
      migrated
      suspended
      suppressed
      stageEntries
      stageExits
      deliveryStatuses
      diagnostics
    }
  }
`;

export const GET_OUTREACH_METRICS = gql`
  query {
    outreachMetrics
  }
`;
export const GET_OUTREACH_CONTACT_HISTORY = gql`
  query ($contactId: ID!, $offset: Int!) {
    outreachContactHistory(contactId: $contactId, offset: $offset, first: 25)
  }
`;

export const GET_CONTINUATION_PREVIEW = gql`
  query ContinuationPreview(
    $sourceVersionId: ID!
    $destinationVersionId: ID!
    $offset: Int
  ) {
    marketingContinuationPreview(
      sourceVersionId: $sourceVersionId
      destinationVersionId: $destinationVersionId
      offset: $offset
    )
  }
`;

export const SEARCH_MARKETING_SCHOOLS = gql`
  query MarketingSchools(
    $workflowId: ID!
    $versionId: ID
    $search: String
    $offset: Int
  ) {
    marketingEligibleSchools(
      workflowId: $workflowId
      versionId: $versionId
      search: $search
      offset: $offset
    )
  }
`;
export const PREVIEW_MARKETING_AUDIENCE = gql`
  query MarketingAudience(
    $workflowId: ID!
    $versionId: ID
    $userIds: [ID!]
    $schoolIds: [ID!]
  ) {
    marketingAudiencePreview(
      workflowId: $workflowId
      versionId: $versionId
      userIds: $userIds
      schoolIds: $schoolIds
    )
  }
`;
